<template>
  <div class="pmp-chart">
    <div class="pmp-head">
      <h3>{{ title }}</h3>
      <div class="pmp-controls">
        <select v-model="metric" class="pmp-metric" aria-label="Métrica">
          <option value="pmp">PMP</option>
          <option value="qty">Quantidade</option>
        </select>
        <label class="pmp-check">
          <input type="checkbox" v-model="includeSpot" />
          Spot
        </label>
        <label class="pmp-check">
          <input type="checkbox" v-model="includeFutures" />
          Futuros
        </label>
        <strong class="pmp-last" style="color: #26a69a">Long {{ lastLongLabel }}</strong>
        <strong class="pmp-last" style="color: #a78bfa">Short {{ lastShortLabel }}</strong>
      </div>
    </div>
    <p v-if="!hasSelection" class="pmp-empty">Selecione spot, futuros ou os dois.</p>
    <div v-else-if="points.length" class="pmp-plot">
      <apexchart
        :key="chartKey"
        type="line"
        height="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
    <p v-else class="pmp-empty">Nenhum evento do período com snapshot.</p>
  </div>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { pmpLevelsFromSnapshot } from '@/utils/computeEdgesSeries.js';

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(4);
}

function formatDelta(longQty, shortQty) {
  const n = Number(longQty || 0) - Number(shortQty || 0);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return sign + n.toFixed(4);
}

export default {
  name: 'EvolPmpChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels: { type: Array, required: true },
  },
  data() {
    return {
      includeSpot: true,
      includeFutures: true,
      metric: 'pmp',
    };
  },
  computed: {
    isQty() {
      return this.metric === 'qty';
    },
    title() {
      return this.isQty ? 'Quantidade Long e Short' : 'PMP Long e PMP Short';
    },
    hasSelection() {
      return this.includeSpot || this.includeFutures;
    },
    points() {
      if (!this.hasSelection) return [];
      return (this.timeSeries || []).map(({ index, m }) => ({
        index,
        ...pmpLevelsFromSnapshot(m?.manualsSnapshot, {
          spot: this.includeSpot,
          futures: this.includeFutures,
        }),
      }));
    },
    lastPoint() {
      return this.points[this.points.length - 1] || null;
    },
    lastLongLabel() {
      if (!this.lastPoint) return '—';
      return this.isQty ? formatQty(this.lastPoint.longQty) : formatPrice(this.lastPoint.pmpLong);
    },
    lastShortLabel() {
      if (!this.lastPoint) return '—';
      return this.isQty ? formatQty(this.lastPoint.shortQty) : formatPrice(this.lastPoint.pmpShort);
    },
    chartKey() {
      const last = this.points[this.points.length - 1];
      return `${this.metric}-${this.includeSpot}-${this.includeFutures}-${this.points.length}-${last?.pmpLong ?? 0}-${last?.longQty ?? 0}-${last?.shortQty ?? 0}`;
    },
    chartSeries() {
      const longName = this.isQty ? 'Qtd Long' : 'PMP Long';
      const shortName = this.isQty ? 'Qtd Short' : 'PMP Short';
      return [
        {
          name: longName,
          data: this.points.map((point) => [
            point.index,
            this.isQty
              ? Number(point.longQty.toFixed(4))
              : (point.pmpLong != null ? Number(point.pmpLong.toFixed(2)) : null),
          ]),
        },
        {
          name: shortName,
          data: this.points.map((point) => [
            point.index,
            this.isQty
              ? Number(point.shortQty.toFixed(4))
              : (point.pmpShort != null ? Number(point.pmpShort.toFixed(2)) : null),
          ]),
        },
      ];
    },
    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        colors: ['#26a69a', '#a78bfa'],
        legend: { show: false },
        stroke: { curve: 'straight', width: 2 },
        markers: { size: 0, hover: { size: 4 }, strokeWidth: 0 },
        grid: {
          ...defaultChartOptions.grid,
          padding: { right: 24, left: 12, top: 8 },
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: {
          labels: { formatter: (val) => (self.isQty ? formatQty(val) : formatPrice(val)) },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom({ dataPointIndex }) {
            return self.tooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    tooltipHtml(dataPointIndex) {
      const point = this.points[dataPointIndex];
      if (!point) return '';
      const label = this.aggLabels[dataPointIndex] || '';
      const delta = Number(point.longQty || 0) - Number(point.shortQty || 0);
      const deltaText = formatDelta(point.longQty, point.shortQty);
      const deltaColor = delta > 0 ? '#4ade80' : delta < 0 ? '#f87171' : '#e5e7eb';
      const spread = point.pmpLong != null && point.pmpShort != null ? point.pmpShort - point.pmpLong : null;
      const spreadText = spread == null
        ? '—'
        : `${spread > 0 ? '+' : spread < 0 ? '-' : ''}$${Math.abs(Math.round(spread)).toLocaleString('en-US')}`;
      const lines = this.isQty
        ? `<div style="color:#26a69a;">Qtd Long: <b>${formatQty(point.longQty)}</b></div>
           <div style="color:#a78bfa;">Qtd Short: <b>${formatQty(point.shortQty)}</b></div>`
        : `<div style="color:#26a69a;">PMP Long: <b>${formatPrice(point.pmpLong)}</b> (${formatQty(point.longQty)})</div>
           <div style="color:#a78bfa;">PMP Short: <b>${formatPrice(point.pmpShort)}</b> (${formatQty(point.shortQty)})</div>
           <div>Short − Long: <b>${spreadText}</b></div>`;
      return `
        <div style="background:#1e1e1e;color:#fff;padding:10px 14px;border:1px solid #444;border-radius:6px;font-size:12px;min-width:220px;">
          <div style="font-weight:600;margin-bottom:8px;border-bottom:1px solid #444;padding-bottom:4px;color:#38bdf8;">${label}</div>
          <div style="line-height:1.7;">
            ${lines}
            <div>Delta: <b style="color:${deltaColor}">${deltaText}</b></div>
          </div>
        </div>
      `;
    },
  },
};
</script>

<style scoped>
.pmp-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--table-header-bgcolor);
  border-radius: 10px;
  padding: 16px 16px 8px;
}

.pmp-head h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #f1f1f1;
}

.pmp-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.pmp-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-color, #f1f1f1);
  cursor: pointer;
}

.pmp-check input[type="checkbox"] {
  margin: 0;
}

.pmp-metric {
  padding: 4px 8px;
  font-size: 13px;
}

.pmp-last {
  font-size: 15px;
  font-weight: 700;
}

.pmp-plot {
  height: calc(100% - 78px);
}

.pmp-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
